import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import { ImageField } from "@/components/MediaUpload";
import { Save, Loader2, ShieldAlert, Wifi, WifiOff, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Small reusable field. */
const Field = ({ label, help, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">{label}</span>
    <div className="mt-1.5">{children}</div>
    {help && <p className="text-[11px] text-[var(--hk-ink-2)] mt-1">{help}</p>}
  </label>
);
const TextInput = (props) => (
  <input {...props} className={"w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] focus:outline-none focus:border-[var(--hk-ink)] " + (props.className || "")} />
);
const NumberInput = ({ value, onChange, ...rest }) => (
  <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]" {...rest} />
);
const Toggle = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2.5 cursor-pointer" data-testid={testId}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
    <span className="text-[13.5px]">{label}</span>
  </label>
);
const Textarea = (props) => (
  <textarea {...props} rows={props.rows || 3} className={"w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px] " + (props.className || "")} />
);

/** Generic settings page wrapper — loads GET /admin/settings/:key, saves PUT same. */
const useSettings = (key) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const pw = useAdminAuthed();
  useEffect(() => {
    if (!pw) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/settings/${key}`, { headers: adminHeaders() });
        setData(r.data || {});
      } catch (e) {
        toast.error(`Failed to load ${key} settings`);
        setData({});
      }
    })();
  }, [key, pw]);
  const save = async (payload) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/${key}`, payload ?? data, { headers: adminHeaders() });
      setData(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return { data, setData, save, saving };
};

// ============ Shipping ============
export const AdminShippingPage = () => {
  const { data, setData, save, saving } = useSettings("shipping");
  if (!data) return <AdminLayout title="Shipping"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;
  const set = (k, v) => setData({ ...data, [k]: v });
  const setListCSV = (k, csv) => setData({ ...data, [k]: csv.split(",").map((s) => s.trim()).filter(Boolean) });
  return (
    <AdminLayout title="Shipping" subtitle="Flat, free-above, remote extras & serviceable pincodes"
      actions={<button onClick={() => save()} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-shipping">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
      </button>}>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="hk-card p-5 space-y-4">
          <h3 className="hk-h5">Base charges (paise)</h3>
          <Field label="Flat rate (₹ ×100)" help="Charged on every order unless free-shipping threshold is met.">
            <NumberInput value={data.flat_rate} onChange={(v) => set("flat_rate", v)} />
          </Field>
          <Field label="Free shipping above (paise)" help={`Currently free above ₹${(data.free_above||0)/100}`}>
            <NumberInput value={data.free_above} onChange={(v) => set("free_above", v)} />
          </Field>
          <Field label="Packaging (paise)"><NumberInput value={data.packaging} onChange={(v) => set("packaging", v)} /></Field>
          <Field label="Handling (paise)"><NumberInput value={data.handling} onChange={(v) => set("handling", v)} /></Field>
        </div>
        <div className="hk-card p-5 space-y-4">
          <h3 className="hk-h5">Order limits & remote areas</h3>
          <Field label="Minimum order value (paise)"><NumberInput value={data.min_order} onChange={(v) => set("min_order", v)} /></Field>
          <Field label="Maximum order value (paise, 0 = no limit)"><NumberInput value={data.max_order} onChange={(v) => set("max_order", v)} /></Field>
          <Field label="Remote area extra charge (paise)"><NumberInput value={data.remote_extra} onChange={(v) => set("remote_extra", v)} /></Field>
          <Field label="Remote states (comma separated)">
            <TextInput value={(data.remote_states || []).join(", ")} onChange={(e) => setListCSV("remote_states", e.target.value)} />
          </Field>
          <Field label="Estimated delivery (days)"><TextInput value={data.estimated_days || ""} onChange={(e) => set("estimated_days", e.target.value)} /></Field>
        </div>
      </div>
      <div className="hk-card p-5 mt-4 space-y-4">
        <h3 className="hk-h5">Serviceable / blocked pincodes</h3>
        <Field label="Serviceable pincodes (comma separated, empty = all)">
          <Textarea value={(data.serviceable_pincodes || []).join(", ")} onChange={(e) => setListCSV("serviceable_pincodes", e.target.value)} />
        </Field>
        <Field label="Blocked pincodes (comma separated)">
          <Textarea value={(data.blocked_pincodes || []).join(", ")} onChange={(e) => setListCSV("blocked_pincodes", e.target.value)} />
        </Field>
      </div>
    </AdminLayout>
  );
};

// ============ Tax / GST ============
export const AdminTaxPage = () => {
  const { data, setData, save, saving } = useSettings("tax");
  if (!data) return <AdminLayout title="Tax / GST"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;
  const set = (k, v) => setData({ ...data, [k]: v });
  return (
    <AdminLayout title="Tax / GST" subtitle="Configure GST — auto-applied at checkout"
      actions={<button onClick={() => save()} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-tax">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
      </button>}>
      <div className="hk-card p-5 space-y-4 max-w-2xl">
        <Toggle checked={data.enabled} onChange={(v) => set("enabled", v)} label="Charge GST on orders" testId="tax-enabled" />
        <Toggle checked={data.prices_inclusive} onChange={(v) => set("prices_inclusive", v)} label="Product prices are inclusive of tax" testId="tax-inclusive" />
        <Field label="Default GST rate (%)"><NumberInput value={data.default_rate} onChange={(v) => set("default_rate", v)} /></Field>
        <Field label="Business GSTIN"><TextInput value={data.gstin || ""} onChange={(e) => set("gstin", e.target.value)} /></Field>
        <Field label="Place of supply (state)"><TextInput value={data.place_of_supply || ""} onChange={(e) => set("place_of_supply", e.target.value)} /></Field>
      </div>
    </AdminLayout>
  );
};

// ============ Payment Gateways ============
const GatewayCard = ({ id, cfg, onChange }) => {
  const set = (k, v) => onChange({ ...cfg, [k]: v });
  const secretKeys = {
    razorpay: [["key_id", "Key ID"], ["key_secret", "Key Secret"], ["webhook_secret", "Webhook Secret"]],
    cashfree: [["client_id", "Client ID"], ["client_secret", "Client Secret"]],
    phonepe: [["merchant_id", "Merchant ID"], ["salt_key", "Salt Key"]],
    stripe: [["publishable_key", "Publishable Key"], ["secret_key", "Secret Key"]],
  }[id] || [];
  return (
    <div className="hk-card p-5 space-y-4" data-testid={`gw-${id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="hk-h5">{cfg.display_name || id}</h3>
        {cfg.enabled ? <Wifi size={14} className="text-emerald-600" /> : <WifiOff size={14} className="text-[var(--hk-ink-2)]" />}
        <span className="text-[11px] px-2 py-0.5 rounded-full border border-[var(--hk-border)]">{cfg.test_mode ? "Test mode" : "Live"}</span>
      </div>
      <Toggle checked={cfg.enabled} onChange={(v) => set("enabled", v)} label={`Enable ${cfg.display_name || id}`} testId={`gw-${id}-enable`} />
      <Toggle checked={cfg.test_mode} onChange={(v) => set("test_mode", v)} label="Test mode" testId={`gw-${id}-test`} />
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Display name"><TextInput value={cfg.display_name || ""} onChange={(e) => set("display_name", e.target.value)} /></Field>
        <Field label="Priority (lower = tried first)"><NumberInput value={cfg.priority || 0} onChange={(v) => set("priority", v)} /></Field>
        {secretKeys.map(([k, l]) => (
          <Field key={k} label={l} help={k.includes("secret") || k.includes("salt") ? "Stored securely; masked after save." : undefined}>
            <TextInput value={cfg[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`gw-${id}-${k}`} />
          </Field>
        ))}
        {id === "cashfree" && (
          <Field label="Cashfree environment" help="Sandbox keys only work with Sandbox; production keys only with Production. Webhook URL: <your-site>/api/payments/cashfree/webhook">
            <select value={cfg.environment || "sandbox"} onChange={(e) => set("environment", e.target.value)} className="hk-input h-10 w-full" data-testid="gw-cashfree-environment">
              <option value="sandbox">Sandbox (test)</option>
              <option value="production">Production (live)</option>
            </select>
          </Field>
        )}
      </div>
      <Field label="Enabled methods (comma separated: upi, card, netbanking, wallet, emi)">
        <TextInput value={(cfg.methods || []).join(", ")} onChange={(e) => set("methods", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
      </Field>
    </div>
  );
};

export const AdminPaymentsPage = () => {
  const { data, setData, save, saving } = useSettings("payment");
  if (!data) return <AdminLayout title="Payment Gateways"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;
  const gws = data.gateways || {};
  const setGw = (id, cfg) => setData({ ...data, gateways: { ...gws, [id]: cfg } });
  return (
    <AdminLayout title="Payment Gateways" subtitle="Configure Razorpay / Cashfree / PhonePe / Stripe"
      actions={<button onClick={() => save()} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-payments">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
      </button>}>
      <div className="hk-card p-5 space-y-3 mb-4 max-w-2xl">
        <div className="flex items-start gap-3">
          <ShieldAlert size={16} className="text-amber-600 mt-0.5" />
          <p className="text-[13px] text-[var(--hk-ink-2)]">
            Secret keys are stored server-side and masked when displayed. Only Super Admin should edit these. Leave a masked field untouched to keep the existing secret.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 pt-2">
          <Field label="Active gateway (used at checkout)">
            <select value={data.active || "razorpay"} onChange={(e) => setData({ ...data, active: e.target.value })}
              className="w-full px-3 py-2 rounded-md border border-[var(--hk-border)] bg-white/70 text-[14px]" data-testid="active-gw">
              {Object.keys(gws).map((k) => <option key={k} value={k}>{gws[k].display_name || k}</option>)}
            </select>
          </Field>
          <div className="pt-6">
            <Toggle checked={!!data.cod_enabled} onChange={(v) => setData({ ...data, cod_enabled: v })}
              label="Enable Cash on Delivery (not recommended for prepaid brand)" testId="cod-toggle" />
          </div>
          <p className="pt-4 text-[12px] text-[var(--hk-ink-3)]">
            The <strong>WhatsApp Order Button</strong> ON/OFF switch lives in <Link to="/admin/settings" className="text-[var(--hk-orange-deep)] underline" data-testid="payments-goto-whatsapp">General Settings</Link>.
          </p>
        </div>
      </div>
      <div className="space-y-4">
        {Object.keys(gws).map((id) => (
          <GatewayCard key={id} id={id} cfg={gws[id]} onChange={(cfg) => setGw(id, cfg)} />
        ))}
      </div>
    </AdminLayout>
  );
};

// ============ Brand ============
export const AdminBrandPage = () => {
  const { data, setData, save, saving } = useSettings("brand");
  if (!data) return <AdminLayout title="Brand"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;
  const set = (k, v) => setData({ ...data, [k]: v });
  return (
    <AdminLayout title="Brand Settings" subtitle="Colours & visual identity"
      actions={<button onClick={() => save()} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-brand">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
      </button>}>
      <div className="hk-card p-5 space-y-4 max-w-2xl">
        <Field label="Primary colour (hex)"><TextInput value={data.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} /></Field>
        <Field label="Accent colour (hex)"><TextInput value={data.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} /></Field>
        <Field label="Text colour on primary (hex)"><TextInput value={data.text_color || ""} onChange={(e) => set("text_color", e.target.value)} /></Field>
      </div>
    </AdminLayout>
  );
};

// ============ SEO & Social ============
export const AdminSeoPage = () => {
  const { data, setData, save, saving } = useSettings("seo");
  if (!data) return <AdminLayout title="SEO"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;
  const set = (k, v) => setData({ ...data, [k]: v });
  return (
    <AdminLayout title="SEO & Social Sharing" subtitle="Default meta tags & Open Graph images used across the site"
      actions={<button onClick={() => save()} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-seo">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
      </button>}>
      <div className="hk-card p-5 space-y-4 max-w-2xl">
        <Field label="Default meta title"><TextInput value={data.default_title || ""} onChange={(e) => set("default_title", e.target.value)} /></Field>
        <Field label="Default meta description"><Textarea value={data.default_description || ""} onChange={(e) => set("default_description", e.target.value)} /></Field>
        <Field label="Open Graph image" help="Used when the site is shared on WhatsApp, Facebook, LinkedIn, Telegram, X.">
          <ImageField value={data.og_image || ""} onChange={(v) => set("og_image", v)} label="" aspect="wide" testid="seo-og-image" />
        </Field>
        <Field label="Twitter card image">
          <ImageField value={data.twitter_image || ""} onChange={(v) => set("twitter_image", v)} label="" aspect="wide" testid="seo-twitter-image" />
        </Field>
        <Field label="Canonical URL"><TextInput value={data.canonical || ""} onChange={(e) => set("canonical", e.target.value)} placeholder="https://herbkranti.com" /></Field>
      </div>
    </AdminLayout>
  );
};

// ============ Global ============
/** WhatsApp Order Button — prominent ON/OFF switch (General Settings). Persists to payment.whatsapp_enabled immediately. */
const WhatsAppOrderButtonCard = ({ whatsappNumber }) => {
  const [enabled, setEnabled] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/settings/payment`, { headers: adminHeaders() })
      .then((r) => setEnabled(r.data?.whatsapp_enabled !== false))
      .catch(() => setEnabled(true));
  }, []);
  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    try {
      const cur = (await axios.get(`${API}/admin/settings/payment`, { headers: adminHeaders() })).data || {};
      await axios.put(`${API}/admin/settings/payment`, { ...cur, whatsapp_enabled: next }, { headers: adminHeaders() });
      setEnabled(next);
      toast.success(next ? "WhatsApp Order Button is now ON at checkout" : "WhatsApp Order Button is now OFF — hidden from customers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };
  return (
    <div className="hk-card p-5 flex flex-wrap items-center gap-5 border-l-4" style={{ borderLeftColor: enabled ? "#25D366" : "var(--hk-border)" }} data-testid="whatsapp-order-button-card">
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: enabled ? "rgba(37,211,102,0.14)" : "rgba(0,0,0,0.05)" }}>
        <MessageCircle size={22} color={enabled ? "#128C7E" : "var(--hk-ink-3)"} />
      </div>
      <div className="flex-1 min-w-[240px]">
        <h3 className="hk-h5">WhatsApp Order Button</h3>
        <p className="mt-1 text-[12.5px] leading-[1.7] text-[var(--hk-ink-3)]">
          Shows the "Order on WhatsApp" option on the checkout page. Turn OFF to hide it from customers — orders will then be accepted through online payment only.
          {whatsappNumber ? <> Messages go to <strong>+{whatsappNumber}</strong>.</> : <> Add your WhatsApp number below so the button works.</>}
        </p>
      </div>
      <button type="button" role="switch" aria-checked={!!enabled} disabled={busy || enabled === null} onClick={toggle}
        className="inline-flex items-center gap-3 disabled:opacity-60" data-testid="whatsapp-toggle">
        <span className={"text-[12px] tracking-[0.14em] uppercase font-medium " + (enabled ? "text-[#128C7E]" : "text-[var(--hk-ink-3)]")} data-testid="whatsapp-toggle-state">
          {enabled === null ? "…" : enabled ? "ON" : "OFF"}
        </span>
        <span className={"relative inline-block w-12 h-7 rounded-full transition-colors " + (enabled ? "bg-[#25D366]" : "bg-[var(--hk-ink-3)]/40")}>
          <span className={"absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform " + (enabled ? "translate-x-[22px]" : "translate-x-0.5")} />
        </span>
      </button>
    </div>
  );
};

// ============ Global / General ============
export const AdminGlobalPage = () => {
  const { data, setData, save, saving } = useSettings("global");
  if (!data) return <AdminLayout title="General Settings"><p className="text-[var(--hk-ink-2)]">Loading…</p></AdminLayout>;
  const set = (k, v) => setData({ ...data, [k]: v });
  const setSocial = (k, v) => setData({ ...data, socials: { ...(data.socials || {}), [k]: v } });
  return (
    <AdminLayout title="General Settings" subtitle="Store controls, company identity, contact, currency & formats"
      actions={<button onClick={() => save()} disabled={saving} className="hk-btn-primary h-10 px-4" data-testid="save-global">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
      </button>}>
      <div className="mb-4"><WhatsAppOrderButtonCard whatsappNumber={data.whatsapp} /></div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="hk-card p-5 space-y-4">
          <h3 className="hk-h5">Company</h3>
          <Field label="Brand name"><TextInput value={data.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} /></Field>
          <Field label="Legal / LLP name"><TextInput value={data.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} /></Field>
          <Field label="Tagline"><TextInput value={data.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></Field>
          <p className="text-[12px] text-[var(--hk-ink-3)]">Logo & favicon are managed in <Link to="/admin/logo" className="text-[var(--hk-orange-deep)] underline" data-testid="global-goto-logo">Logo Management</Link>.</p>
          <Field label="Registered address"><Textarea value={data.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="GSTIN"><TextInput value={data.gstin || ""} onChange={(e) => set("gstin", e.target.value)} /></Field>
          <Field label="FSSAI Licence"><TextInput value={data.fssai || ""} onChange={(e) => set("fssai", e.target.value)} /></Field>
          <Field label="CIN / LLPIN"><TextInput value={data.cin || ""} onChange={(e) => set("cin", e.target.value)} /></Field>
        </div>
        <div className="hk-card p-5 space-y-4">
          <h3 className="hk-h5">Contact & Format</h3>
          <Field label="Support email"><TextInput value={data.support_email || ""} onChange={(e) => set("support_email", e.target.value)} /></Field>
          <Field label="Support phone"><TextInput value={data.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} /></Field>
          <Field label="WhatsApp number (with country code, no +)"><TextInput value={data.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
          <Field label="Currency"><TextInput value={data.currency || "INR"} onChange={(e) => set("currency", e.target.value)} /></Field>
          <Field label="Time zone"><TextInput value={data.timezone || "Asia/Kolkata"} onChange={(e) => set("timezone", e.target.value)} /></Field>
          <Field label="Date format"><TextInput value={data.date_format || ""} onChange={(e) => set("date_format", e.target.value)} /></Field>
          <Field label="Order prefix"><TextInput value={data.order_prefix || ""} onChange={(e) => set("order_prefix", e.target.value)} /></Field>
          <Field label="Invoice prefix"><TextInput value={data.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} /></Field>
          <h3 className="hk-h5 pt-2">Social profiles</h3>
          {["instagram", "youtube", "facebook", "linkedin", "x"].map((s) => (
            <Field key={s} label={s.charAt(0).toUpperCase() + s.slice(1)}>
              <TextInput value={(data.socials || {})[s] || ""} onChange={(e) => setSocial(s, e.target.value)} placeholder="https://..." />
            </Field>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};
